import { useMutation } from '@apollo/client/react';
import { Fragment, useState } from 'react';
import { toast } from 'sonner';
import { ArrowRight, CircleAlert } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ClientError, getApiError } from 'src/utils/errors';
import { EscrowPartyTypeEnum, type IBeneficiary, type IDepositor } from 'src/graphql/schema';
import { EscrowTerminationAcceptDocument } from 'src/graphql/actions/escrow/escrowTerminationAccept.mutation';
import { EscrowTerminationRejectDocument } from 'src/graphql/actions/escrow/escrowTerminationReject.mutation';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { OrganizationLogo } from 'src/components/Organization';

const counterpartyType = {
  [EscrowPartyTypeEnum.Beneficiary]: EscrowPartyTypeEnum.Depositor,
  [EscrowPartyTypeEnum.Depositor]: EscrowPartyTypeEnum.Beneficiary,
};

type Props = {
  triggerNode: React.ReactNode;
};

const ReviewRequestTerminationDialog = ({ triggerNode }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [successDialog, setSuccessDialog] = useState<{
    isOpen: boolean;
    verdict: 'approved' | 'rejected' | null;
  }>({
    isOpen: false,
    verdict: null,
  });
  const [rejectEscrowTermination, { loading: rejectLoading }] = useMutation(EscrowTerminationRejectDocument);
  const [acceptEscrowTermination, { loading: acceptLoading }] = useMutation(EscrowTerminationAcceptDocument);
  const {
    softwareEscrow,
    softwareEscrow: { escrowTermination },
    refetch,
  } = useSoftwareEscrow();

  const onRejectTermination = async () => {
    try {
      const { data } = await rejectEscrowTermination({
        variables: { escrowTerminationId: escrowTermination?.id as string },
      });
      if (data?.escrowTerminationReject?.success) {
        setIsOpen(false);
        setSuccessDialog({
          isOpen: true,
          verdict: 'rejected',
        });
      } else {
        throw new ClientError(data?.escrowTerminationReject?.errors?.[0]);
      }
    } catch (error: unknown) {
      getApiError(error, toast.error);
    }
  };

  const onApproveTermination = async () => {
    try {
      const { data } = await acceptEscrowTermination({
        variables: { escrowTerminationId: escrowTermination?.id as string },
      });
      if (data?.escrowTerminationAccept?.success) {
        setIsOpen(false);
        setSuccessDialog({
          isOpen: true,
          verdict: 'approved',
        });
      } else {
        throw new ClientError(data?.escrowTerminationAccept?.errors?.[0]);
      }
    } catch (error: unknown) {
      getApiError(error, toast.error);
    }
  };

  const companies = [
    softwareEscrow[softwareEscrow.startedBy as EscrowPartyTypeEnum] as IBeneficiary | IDepositor,
    softwareEscrow[counterpartyType[softwareEscrow.startedBy as EscrowPartyTypeEnum]] as IBeneficiary | IDepositor,
  ].filter(Boolean);

  const onSuccessOpenChange = (open: boolean) => {
    if (!open) {
      refetch();
      setSuccessDialog({ isOpen: false, verdict: null });
    }
  };

  return (
    <Fragment>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>{triggerNode}</DialogTrigger>
        <DialogContent className="sm:max-w-[676px]">
          <div className="flex items-center gap-2.5">
            {companies.map((company, index) => (
              <Fragment key={company.id}>
                <div className="flex items-center gap-3 p-2.5">
                  <OrganizationLogo logo={company.companyWebsite ?? ''} name={company.companyName ?? ''} size="sm" />
                  <div className="text-xl text-foreground font-bold">{company.companyName}</div>
                </div>
                {index < companies.length - 1 && <ArrowRight className="shrink-0 size-5 text-muted-foreground" />}
              </Fragment>
            ))}
          </div>
          <DialogHeader>
            <DialogTitle>Do you approve termination of this escrow?</DialogTitle>
            <DialogDescription>
              If you approve the termination request, the escrow will stay active during the notice period in the
              agreement. Afterward, the data will be removed, and escrow protection will end.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-card-foreground text-sm">
              <CircleAlert className="size-3.5 text-orange-700 shrink-0" />
              <span className="font-semibold">For agreements not yet finalized</span>
            </div>
            <DialogDescription>If the agreement isn’t fully executed, the notice period won’t apply.</DialogDescription>
          </div>
          <DialogFooter className="pt-2.5">
            <Button variant="outline" onClick={onRejectTermination} isLoading={rejectLoading}>
              Reject termination
            </Button>
            <Button variant="destructive" onClick={onApproveTermination} isLoading={acceptLoading}>
              Approve termination
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={successDialog.isOpen} onOpenChange={onSuccessOpenChange}>
        <DialogContent className="sm:max-w-[441px]">
          <DialogHeader className="text-center">
            <DialogTitle className="text-lg">
              Escrow termination request {successDialog.verdict === 'approved' ? 'approved' : 'rejected'}
            </DialogTitle>
            <DialogDescription>
              The termination request has been submitted to Codekeeper for final review. We’ll notify you of the outcome
              once the review is complete.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button className="w-full">Back to dashboard</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};

export default ReviewRequestTerminationDialog;
